import { observer } from 'mobx-react-lite'
import { useEffect, useRef, useState } from 'react'
import { toApiError } from '@/api/errors'
import { Button } from '@/components/ui/Button'
import { InlineAlert } from '@/components/ui/InlineAlert'
import { useT } from '@/i18n'
import { formatShortDate, formatShortDateTime } from '@/lib/format'
import { exchangeRatesService, type CbuStatus, type CbuSyncResult } from '@/services/finance/exchangeRates.service'
import { useStores } from '@/stores'

const CAPABILITY = 'finance_exchange_rate_versions.create'

/**
 * «Курсы ЦБ» (ALFI TEST #26): the server reads the Central Bank feed itself
 * (through the cb-currency.udevs.io relay, because cbu.uz drops DigitalOcean
 * IPs). The browser never reads cbu.uz. «Загрузить файл» stays as a fallback.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export async function loadCbuRates(_feedUrl?: string): Promise<CbuSyncResult> {
  return exchangeRatesService.cbuSync()
}

/** Whether the newest day loaded is older than today in Tashkent. */
function stale(status: CbuStatus) {
  return !status.last || status.last.rate_date < status.today
}

/**
 * Loads today's «Курсы ЦБ» once a day per profile, quietly, as soon as someone
 * who may add rates opens the admin. The server's own job (00:10, 09:10, 18:10)
 * keeps trying as well.
 */
export function useCbuAutoLoad() {
  const { session } = useStores()
  const profileId = session.profile?.id ?? ''
  const allowed = session.can(CAPABILITY)
  useEffect(() => {
    if (!allowed || !profileId) return
    const key = `mf.cbu.${profileId}`
    const today = new Date().toDateString()
    if (localStorage.getItem(key) === today) return
    localStorage.setItem(key, today)
    exchangeRatesService
      .cbuStatus()
      .then((status) => (stale(status) ? loadCbuRates(status.feed_url) : undefined))
      .catch(() => undefined)
  }, [allowed, profileId])
}

/**
 * The «Курсы ЦБ» line of «Системные курсы» and «Конвертация»: the day loaded,
 * its source and when, with «Обновить из ЦБ» and «Загрузить файл» (a JSON
 * saved from cbu.uz).
 */
export const CbuRatesPanel = observer(function CbuRatesPanel({ onLoaded }: { onLoaded?: () => void }) {
  const t = useT()
  const { session, toasts } = useStores()
  const canLoad = session.can(CAPABILITY)
  const [status, setStatus] = useState<CbuStatus | null>(null)
  const [busy, setBusy] = useState<'' | 'refresh' | 'file'>('')
  const picker = useRef<HTMLInputElement>(null)

  const readStatus = () =>
    exchangeRatesService
      .cbuStatus()
      .then(setStatus)
      .catch(() => undefined)
  useEffect(() => {
    void readStatus()
  }, [])

  const done = (result: CbuSyncResult) => {
    const date = formatShortDate(result.rate_date)
    toasts.success(result.created ? t('finance.exchangeRates.cbu.loaded', { date }) : t('finance.exchangeRates.cbu.already', { date }))
    void readStatus()
    if (result.created) onLoaded?.()
  }

  const refresh = async () => {
    if (!status) return
    setBusy('refresh')
    try {
      done(await loadCbuRates(status.feed_url))
    } catch (cause) {
      toasts.error(t('finance.exchangeRates.cbu.failed'), toApiError(cause).message)
    } finally {
      setBusy('')
    }
  }

  const upload = async (file?: File) => {
    if (!file) return
    setBusy('file')
    try {
      const text = await file.text()
      let feed: unknown = text
      try {
        feed = JSON.parse(text)
      } catch {
        // Sent as is: the backend answers that it is not a cbu.uz feed, in the user's language.
      }
      done(await exchangeRatesService.cbuImport(feed, 'file'))
    } catch (cause) {
      toasts.error(t('finance.exchangeRates.cbu.failed'), toApiError(cause).message)
    } finally {
      setBusy('')
      if (picker.current) picker.current.value = ''
    }
  }

  if (!status) return null
  const last = status.last
  const summary = last
    ? t('finance.exchangeRates.cbu.last', {
        date: formatShortDate(last.rate_date),
        source: t(`finance.exchangeRates.sources.${last.source}`),
        at: formatShortDateTime(last.created_at),
      })
    : t('finance.exchangeRates.cbu.never')

  return (
    <InlineAlert tone={stale(status) ? 'warning' : 'info'}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="min-w-0 flex-1">
          <span className="font-medium">{summary}</span>
          {stale(status) && <span> {t('finance.exchangeRates.cbu.stale', { today: formatShortDate(status.today) })}</span>}
          <span className="block text-[length:calc(12.5px*var(--text-scale))] opacity-80">{t('finance.exchangeRates.cbu.hint')}</span>
        </span>
        {canLoad && (
          <span className="flex shrink-0 gap-2">
            <Button size="sm" variant="secondary" loading={busy === 'refresh'} disabled={busy !== ''} onClick={() => void refresh()}>
              {t('finance.exchangeRates.cbu.refresh')}
            </Button>
            <Button size="sm" variant="ghost" loading={busy === 'file'} disabled={busy !== ''} onClick={() => picker.current?.click()}>
              {t('finance.exchangeRates.cbu.upload')}
            </Button>
            <input ref={picker} type="file" accept="application/json,.json" hidden onChange={(event) => void upload(event.target.files?.[0])} />
          </span>
        )}
      </div>
    </InlineAlert>
  )
})
